import React from "react";
import PropTypes from "prop-types";
import moment from "moment";

import { Col } from "react-flexbox-grid/lib/index";
import {
  Table,
  TableRow,
  TableBody,
  TableHead,
  TableCell,
  IconButton
} from "@material-ui/core";
import CheckCircle from "@material-ui/icons/CheckBox";
import { getSoloPhysNum } from "util/solo";

// aligning headers with table rows https://github.com/callemall/material-ui/issues/1911

const styles = {
  miscastStyle: {
    backgroundColor: "#e4e3de",
    opacity: 0.5
  },
  headerStyle: {
    marginTop: 5,
    fontWeight: "bold"
  }
};

const CastsInFileTable = props => (
  <Col xs={12}>
    {props.soloFilesBeingConfirmed.map(filePk => {
      const fileInfo = props.soloConfirmFiles[filePk];
      let castKeys = [];
      if (props.soloCastsInFiles[filePk]) {
        castKeys = Object.keys(props.soloCastsInFiles[filePk]);
      }
      const miscasts = castKeys.filter(
        castPk => props.soloCastsInFiles[filePk][castPk].miscast === true
      );
      let headerInfo;
      let bodyStyle = {};
      if (fileInfo.processingStage === "annotated") {
        headerInfo = `Annotated: ${moment(fileInfo.annotatedDate).format(
          "lll"
        )} \u00A0\u00A0
      Survey: ${moment(fileInfo.collectionDt).format("ll")} \u00A0\u00A0
      Solo: ${getSoloPhysNum(
        fileInfo.deviceSn,
        props.soloUIDLookup
      )} \u00A0\u00A0
      Miscasts: ${miscasts.length} \u00A0\u00A0
      Filename: ${fileInfo.filename}`;
      } else {
        let reason = (fileInfo.fileComments ?? "").substring(0, 40);
        if (reason.length === 40) reason += "...";
        headerInfo = `Unable to annotate: ${moment(
          fileInfo.annotatedDate
        ).format("lll")} \u00A0\u00A0
        Survey: ${moment(fileInfo.collectionDt).format("ll")} \u00A0\u00A0
        Solo: ${getSoloPhysNum(
          fileInfo.deviceSn,
          props.soloUIDLookup
        )} \u00A0\u00A0
        Reason: ${reason}`;
        bodyStyle = styles.miscastStyle;
      }
      return (
        <Table key={filePk} bodyStyle={{ overflow: "visible" }}>
          <TableHead>
            <TableRow>
              <TableCell
                colSpan="8"
                style={{
                  width: 240,
                  fontWeight: "400",
                  fontSize: "14.5px",
                  color: props.soloFilesToRecall.includes(filePk)
                    ? "#B60C0F"
                    : "black"
                }}
              >
                <IconButton
                  title="Recall"
                  onClick={() => props.onToggleRecallClick(filePk)}
                >
                  <CheckCircle
                    color={
                      props.soloFilesToRecall.includes(filePk)
                        ? "#B60C0F"
                        : "#C9C7BE"
                    }
                  />
                </IconButton>
                {headerInfo}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell style={{ width: 40 }}>Site Id</TableCell>
              <TableCell style={{ width: 40 }}>Survey</TableCell>
              <TableCell style={{ width: 40 }}>Drop</TableCell>
              <TableCell style={{ width: 40 }}>Form depth</TableCell>
              <TableCell style={{ width: 40 }}>Instrument depth</TableCell>
              <TableCell style={{ width: 40 }}>Messenger drop</TableCell>
              <TableCell style={{ width: 40 }}>Cast bottom time</TableCell>
              <TableCell style={{ width: 40 }}>Comments</TableCell>
            </TableRow>
          </TableHead>
          <TableBody />
          {castKeys.length > 0 && (
            <TableBody displayRowCheckbox={false} style={bodyStyle}>
              {castKeys.map(castPk => {
                const cast = props.soloCastsInFiles[filePk][castPk];
                const targetDepth = cast.targetDepthM
                  ? `${cast.targetDepthM} m`
                  : ``;
                const castCollected = cast.collected
                  ? moment(cast.collected).format("LT")
                  : ``;
                const rowStyle = cast.miscast ? styles.miscastStyle : {};
                return (
                  <TableRow key={castPk} selectable={false} style={rowStyle}>
                    <TableCell style={{ width: 40 }} title={cast.siteId}>
                      {cast.siteId}
                    </TableCell>
                    <TableCell style={{ width: 40 }} title={cast.survey}>
                      {cast.survey}
                    </TableCell>
                    <TableCell style={{ width: 40 }} title={cast.drop}>
                      {cast.drop}
                    </TableCell>
                    <TableCell style={{ width: 40 }} title={cast.targetDepthM}>
                      {targetDepth}
                    </TableCell>
                    <TableCell style={{ width: 40 }} title={cast.bottomDepth}>
                      {`${Math.round(cast.bottomDepth * 10) / 10} m`}
                    </TableCell>
                    <TableCell style={{ width: 40 }} title={cast.collected}>
                      {castCollected}
                    </TableCell>
                    <TableCell style={{ width: 40 }} title={cast.bottomDt}>
                      {moment(cast.bottomDt).format("LT")}
                    </TableCell>
                    <TableCell style={{ width: 40 }} title={cast.comments}>
                      {cast.comments}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          )}
        </Table>
      );
    })}
  </Col>
);

const fileShape = PropTypes.shape({
  filename: PropTypes.string.isRequired,
  collectionDt: PropTypes.string,
  numCasts: PropTypes.number,
  annotatedDate: PropTypes.string,
  deviceSn: PropTypes.string,
  fileComments: PropTypes.string,
  processingStage: PropTypes.string
});

const castShape = PropTypes.shape({
  collected: PropTypes.string,
  targetDepthM: PropTypes.number,
  siteId: PropTypes.string,
  drop: PropTypes.number,
  bottomDepth: PropTypes.number,
  bottomDt: PropTypes.string,
  survey: PropTypes.arrayOf(PropTypes.string)
});
CastsInFileTable.propTypes = {
  soloFilesToRecall: PropTypes.arrayOf(PropTypes.number).isRequired,
  soloFilesBeingConfirmed: PropTypes.arrayOf(PropTypes.number).isRequired,
  soloConfirmFiles: PropTypes.objectOf(fileShape),
  soloCastsInFiles: PropTypes.objectOf(castShape),
  // eslint-disable-next-line react/forbid-prop-types
  soloUIDLookup: PropTypes.object.isRequired,
  onToggleRecallClick: PropTypes.func.isRequired
};

CastsInFileTable.defaultProps = {
  soloCastsInFiles: {},
  soloConfirmFiles: {}
};

export default CastsInFileTable;
